import React, { useMemo, useState } from 'react'
import { Alert, DatePicker, Form, Table, Typography } from 'antd'
import type { TableColumnsType } from 'antd'
import { Dayjs } from 'dayjs'
import {
  MonthlySummaryRow,
  MonthlySummaryTotals,
  useMonthlySummaryQuery,
} from '../../api/reconciliation-v2-reports-api-client'
import {
  formatMoney,
  sourceLabel,
} from '../../components/TransactionDetails/helpers'
import { BLANK_SOURCE_LABEL, currentMonthLA } from './helpers'

const { Text } = Typography

const Difference = ({ value }: { value: number }) => (
  <Text type={value < 0 ? 'danger' : undefined}>{formatMoney(value)}</Text>
)

const COLUMNS: TableColumnsType<MonthlySummaryRow> = [
  {
    title: 'Source',
    dataIndex: 'source',
    key: 'source',
    render: (source: string | null) =>
      source === null ? BLANK_SOURCE_LABEL : sourceLabel(source),
  },
  {
    title: 'Benefit-months',
    dataIndex: 'benefitMonthCount',
    key: 'benefitMonthCount',
    align: 'right',
  },
  {
    title: 'Expected',
    dataIndex: 'expected',
    key: 'expected',
    align: 'right',
    render: (value: number) => formatMoney(value),
  },
  {
    title: 'Actual',
    dataIndex: 'actual',
    key: 'actual',
    align: 'right',
    render: (value: number) => formatMoney(value),
  },
  {
    title: 'Difference',
    dataIndex: 'difference',
    key: 'difference',
    align: 'right',
    render: (value: number) => <Difference value={value} />,
  },
]

const GrandTotalRow = ({ totals }: { totals: MonthlySummaryTotals }) => (
  <Table.Summary.Row>
    <Table.Summary.Cell index={0}>
      <Text strong>Grand Total</Text>
    </Table.Summary.Cell>
    <Table.Summary.Cell index={1} align="right">
      <Text strong>{totals.benefitMonthCount}</Text>
    </Table.Summary.Cell>
    <Table.Summary.Cell index={2} align="right">
      <Text strong>{formatMoney(totals.expected)}</Text>
    </Table.Summary.Cell>
    <Table.Summary.Cell index={3} align="right">
      <Text strong>{formatMoney(totals.actual)}</Text>
    </Table.Summary.Cell>
    <Table.Summary.Cell index={4} align="right">
      <Text strong>
        <Difference value={totals.difference} />
      </Text>
    </Table.Summary.Cell>
  </Table.Summary.Row>
)

/**
 * Reconciliation monthly summary: per transaction source, expected (by premium
 * source — the paying source when paid, else the benefit's current assigned
 * method) vs actual (allocated, by paying source) for one coverage month.
 * "(blank)" holds unpaid benefits with no recognizable assigned method.
 */
export default function MonthlySummaryTab() {
  const [month, setMonth] = useState<Dayjs>(currentMonthLA)
  const params = useMemo(
    () => ({ year: month.year(), month: month.month() + 1 }),
    [month]
  )
  const { data, isLoading, isFetching, error } = useMonthlySummaryQuery(params)

  return (
    <>
      <Form.Item label="Coverage month" layout="vertical">
        <DatePicker
          picker="month"
          format="MMMM YYYY"
          allowClear={false}
          value={month}
          onChange={(value) => value && setMonth(value.startOf('month'))}
          aria-label="Coverage month"
        />
      </Form.Item>

      {error && (
        <Alert
          type="error"
          showIcon
          message="Could not load the monthly summary."
          style={{ marginBottom: 16 }}
        />
      )}

      {data && data.totals.unknownExpectedCount > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`${data.totals.unknownExpectedCount} benefit-month(s) have no expected price and are counted as $0 expected.`}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table<MonthlySummaryRow>
        rowKey={(row) => row.source ?? 'blank'}
        columns={COLUMNS}
        dataSource={data?.rows ?? []}
        loading={isLoading || isFetching}
        pagination={false}
        bordered
        summary={() => (data ? <GrandTotalRow totals={data.totals} /> : null)}
      />
    </>
  )
}
